import { useState, useEffect } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { useChat } from "@/hooks/useChat";

const SUGGESTED = [
  "What is Category 3 water?",
  "How do I interpret mold air sampling?",
  "Do I need post-remediation verification?",
];

function ChatMessage({ role, content, pending }) {
  return (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-message-${role}`}
        className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          role === "user"
            ? "bg-[#7C3AED] text-white rounded-br-sm"
            : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
        }`}
      >
        {content || (pending ? "…" : "")}
      </div>
    </div>
  );
}

function ChatInput({ value, onChange, onSend, disabled }) {
  return (
    <div className="p-3 border-t border-slate-200 bg-white">
      <div className="flex items-center gap-2">
        <input
          data-testid="ai-assistant-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSend()}
          placeholder="Ask about mold, IAQ, water damage…"
          disabled={disabled}
          className="flex-1 px-4 py-3 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-[#7C3AED] disabled:opacity-60"
        />
        <button
          data-testid="ai-assistant-send"
          onClick={onSend}
          disabled={disabled || !value.trim()}
          className="w-11 h-11 rounded-full bg-[#7C3AED] text-white grid place-items-center disabled:opacity-50 hover:bg-[#6D28D9]"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const { messages, input, setInput, streaming, scrollRef, send } = useChat();

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, scrollRef]);

  return (
    <>
      {!open && (
        <button
          data-testid="ai-assistant-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-[60] w-16 h-16 rounded-full bg-[#7C3AED] text-white grid place-items-center shadow-2xl hover:scale-105 transition-transform"
          aria-label="Open AI assistant"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-20 right-6 z-[60] w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-7rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="bg-[#0F172A] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#7C3AED] grid place-items-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold text-sm">Hurtler AI Assistant</div>
                <div className="text-[11px] text-white/60">Powered by Claude • Environmental expert</div>
              </div>
            </div>
            <button
              data-testid="ai-assistant-close"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-5 space-y-4 bg-[#F8FAFC]">
            {messages.map((m, i) => (
              <ChatMessage
                key={m.id}
                role={m.role}
                content={m.content}
                pending={streaming && i === messages.length - 1}
              />
            ))}

            {messages.length === 1 && (
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Try asking
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`ai-suggested-${i}`}
                      onClick={() => setInput(s)}
                      className="text-xs px-3 py-2 rounded-full bg-white border border-slate-200 hover:border-[#7C3AED] hover:text-[#7C3AED] text-slate-700"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <ChatInput value={input} onChange={setInput} onSend={() => send(input)} disabled={streaming} />
        </div>
      )}
    </>
  );
}
